"use client";

import React, { useEffect } from "react";
import { MapPin, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function LocationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage || "Location checked successfully!", {
        description: "We cover this area! One of our team will be in touch shortly.",
        icon: <CheckCircle2 className="h-5 w-5 text-green-500" />
      });
      resetForm();
    } else if (submitError) {
      toast.error("Something went wrong", {
        description: submitError,
        icon: <AlertTriangle className="h-5 w-5 text-destructive" />
      });
    }
  }, [isSubmitted, submitError, successMessage, resetForm]);
  return <Form onSubmit={handleSubmit} className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
        {}
        <div className="md:col-span-4">
          <FormField control={control} name="postcode" rules={{
          required: "Postcode is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="sr-only">Postcode</FormLabel>
                <FormControl>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Enter Postcode (e.g. NR1 1AA)" className="pl-9 bg-background/50 border-input/50 focus:bg-background transition-colors h-11" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="md:col-span-3">
          <FormField control={control} name="email" rules={{
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="sr-only">Email Address</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="Email (Optional)" className="bg-background/50 border-input/50 focus:bg-background transition-colors h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="md:col-span-3">
          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[0-9\s+()-]+$/,
            message: "Invalid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="sr-only">Phone Number</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="Phone (Optional)" className="bg-background/50 border-input/50 focus:bg-background transition-colors h-11" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="md:col-span-2">
          <Button type="submit" disabled={isSubmitting} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide">
            {isSubmitting ? <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </> : "Check Now"}
          </Button>
        </div>
      </div>
      <div className="mt-2 text-center md:text-left">
        <p className="text-xs text-muted-foreground">
          * Enter your details to check availability and receive a confirmation.
        </p>
      </div>
    </Form>;
}
export default function CheckYourLocation() {
  return <section id="check-your-location" className="dark bg-background text-foreground relative overflow-hidden border-t border-border">
      {}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-[repeating-linear-gradient(45deg,var(--primary),var(--primary)_10px,var(--primary-foreground)_10px,var(--primary-foreground)_20px)] opacity-80" />

      <div className="container mx-auto px-4 py-12 md:py-16 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          {}
          <div className="w-full lg:w-1/3 text-center lg:text-left space-y-4">
            <div className="inline-flex items-center justify-center lg:justify-start gap-2 text-primary font-bold uppercase tracking-wider text-sm">
              <AlertTriangle className="h-4 w-4" />
              <span>Service Area Check</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              Just outside our area?
            </h2>
            <p className="text-muted-foreground text-lg">
              We might still be able to help. Enter your postcode to check if our
              mobile units can reach you.
            </p>
          </div>

          {}
          <div className="w-full lg:w-2/3 bg-card/50 backdrop-blur-sm border border-border/50 rounded-xl p-6 md:p-8 shadow-lg">
            <FormProvider formId="location_check_form_areas_covered" sectionName="check-your-location" defaultValues={{
            postcode: "",
            email: "",
            phone: ""
          }} validationRules={{
            postcode: {
              required: true
            },
            email: {
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            }
          }} submitText="Check Location" successMessage="Good news! We cover this area." errorMessage="We couldn't verify this location. Please call us directly." className="w-full">
              <LocationForm />
            </FormProvider>
          </div>
        </div>
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-full opacity-30" />
      </div>
    </section>;
}